import React from "react";
var __jsx = React.createElement;
import { useState, useEffect, useRef } from "react";
import ProgressiveImage from "@componentsShared/ProgressiveImage";
import { useLang } from "@/common/Contexts/LanguageContext";
import { twMerge } from "tailwind-merge";
import useIsMobile from "@hooks/useIsMobile";
import { CDN_LINK } from "@globalConfig";
import { trackEvents } from "@/common/events/events";
var HomeBanner = function HomeBanner(_ref) {
  var heros = _ref.heros;
  var _useState = useState(0),
    activeImage = _useState[0],
    setActiveImage = _useState[1];
  var lang = useLang();
  var autoLoopInterval = useRef(null);
  var isMobile = useIsMobile();
  var _useState2 = useState(0),
    dragOffset = _useState2[0],
    setDragOffset = _useState2[1];
  var touchStartX = useRef(0);
  var touchMoveX = useRef(0);
  var isDragging = useRef(false);
  var isRTL = lang === "ar";
  var handleTouchStartSlide = function handleTouchStartSlide(e) {
    stopAutoLoop();
    touchStartX.current = e.touches[0].clientX;
    touchMoveX.current = touchStartX.current;
    setDragOffset(0); // Reset previous drag
    isDragging.current = true;
  };
  var handleTouchMoveSlide = function handleTouchMoveSlide(e) {
    if (!isDragging.current) return;
    touchMoveX.current = e.touches[0].clientX;
    var deltaX = touchMoveX.current - touchStartX.current;

    //        setDragOffset(deltaX); // Apply real-time movement

    if (Math.abs(deltaX) > 10) {
      isDragging.current = false;
      if (deltaX > 0 && !isRTL || deltaX < 0 && isRTL) {
        handlePrev();
      } else {
        handleNext();
      }
    }
  };
  var handleTouchEndSlide = function handleTouchEndSlide() {
    isDragging.current = false;
    setDragOffset(0);
    startAutoLoop();
  };
  var startAutoLoop = function startAutoLoop() {
    stopAutoLoop();
    autoLoopInterval.current = setInterval(function () {
      setActiveImage(function (prev) {
        return (prev + 1) % heros.length;
      });
    }, 5000);
  };

  // Handles previous button click
  var handlePrev = function handlePrev() {
    stopAutoLoop();
    setActiveImage(function (prev) {
      return prev === 0 ? heros.length - 1 : prev - 1;
    });
    startAutoLoop();
  };

  // Handles next button click
  var handleNext = function handleNext() {
    stopAutoLoop();
    setActiveImage(function (prev) {
      return (prev + 1) % heros.length;
    });
    startAutoLoop();
  };
  var setActiveIndex = function setActiveIndex(index) {
    stopAutoLoop(); // Stop auto loop when user clicks a dot
    setActiveImage(index); // Update the active image index
    startAutoLoop(); // Restart auto loop after interaction
  };
  var stopAutoLoop = function stopAutoLoop() {
    if (autoLoopInterval.current) {
      clearInterval(autoLoopInterval.current);
      autoLoopInterval.current = null;
    }
  };
  useEffect(function () {
    startAutoLoop();
    return function () {
      return stopAutoLoop();
    };
  }, [heros.length]);
  var getBannerClasses = function getBannerClasses(idx) {
    var base = "block pb-[33.33%] overflow-hidden w-full transition-transform duration-100 ease-in-out max-md:pb-[44.33%] max-md:rounded-[8px]";
    if (idx === activeImage) return "".concat(base, " relative");
    if (activeImage === 0 && idx === heros.length - 1 && heros.length > 2) return "".concat(base, " absolute top-0 ").concat(isRTL ? "translate-x-[calc(100%+12px)]" : "-translate-x-[calc(100%+12px)]");
    if (activeImage === heros.length - 1 && idx === 0 && heros.length > 2) return "".concat(base, " absolute top-0 ").concat(isRTL ? "-translate-x-[calc(100%+12px)]" : "translate-x-[calc(100%+12px)]");
    return idx < activeImage ? "".concat(base, " absolute top-0 ").concat(isRTL ? "translate-x-[calc(100%+12px)]" : "-translate-x-[calc(100%+12px)]") : "".concat(base, " absolute top-0 ").concat(isRTL ? "-translate-x-[calc(100%+12px)]" : "translate-x-[calc(100%+12px)]");
  };
  var getBannerStyle = function getBannerStyle(idx) {
    if (activeImage !== idx) return;
    var translateX = 0;
    if (idx === activeImage) {
      translateX = dragOffset; // Move active image with the touch
    } else if (idx < activeImage) {
      translateX = -window.innerWidth + dragOffset; // Move left images
    } else {
      translateX = window.innerWidth + dragOffset; // Move right images
    }
    return {
      transform: "translateX(".concat(translateX, "px)"),
      transition: isDragging.current ? "none" : "transform 0.3s ease-in-out"
    };
  };
  var getZIndex = function getZIndex(idx) {
    return idx === activeImage ? 3 : idx === activeImage + 1 ? 2 : idx === heros.length - 1 ? -1 : 1;
  };
  return /*#__PURE__*/React.createElement("div", {
    className: "overflow-hidden"
  }, /*#__PURE__*/React.createElement("div", {
    className: "relative max-md:mx-auto max-md:w-[90%]",
    onTouchStart: handleTouchStartSlide,
    onTouchMove: handleTouchMoveSlide,
    onTouchEnd: handleTouchEndSlide
  }, heros === null || heros === void 0 ? void 0 : heros.map(function (item, idx) {
    var navLink = "".concat(lang === "en" ? "/en" : "").concat(item !== null && item !== void 0 && item.cta_filter ? "/filters" + (item === null || item === void 0 ? void 0 : item.cta_filter) + "#referral_location=Home&browse_variant=hero_area&variant_value=".concat(item === null || item === void 0 ? void 0 : item.id) : (item === null || item === void 0 ? void 0 : item.cta_url_path) + "#referral_location=Home&browse_variant=hero_area&variant_value=".concat(item === null || item === void 0 ? void 0 : item.id));
    var gradientColor = item.gradient_direction && item.gradient_enable !== 0 ? "linear-gradient(".concat(item.gradient_direction, ", ").concat(item.primary_banner_color, " 0%, ").concat(item.secondary_banner_color, " 100%)") : item.primary_banner_color;
    var loadingType = "lazy";
    if (idx === 0 || idx === 1 && isMobile || idx === heros.length - 1 && isMobile) {
      loadingType = "eager"; // Load first, second (only for mobile), and last images eagerly
    }
    var hasLink = isMobile ? (item === null || item === void 0 ? void 0 : item.cta_mobile_enable) === 1 : (item === null || item === void 0 ? void 0 : item.cta_desktop_enable) === 1;
    return /*#__PURE__*/React.createElement("a", {
      key: idx + "HomeBanner",
      href: hasLink && (item !== null && item !== void 0 && item.cta_filter || item !== null && item !== void 0 && item.cta_url_path) ? navLink : "#",
      className: [twMerge(getBannerClasses(idx)), !(item !== null && item !== void 0 && item.cta_filter || item !== null && item !== void 0 && item.cta_url_path) ? "pointer-events-none" : undefined].join(" "),
      style: {
        //  ...getBannerStyle(idx),
        background: gradientColor,
        zIndex: getZIndex(idx)
      },
      onClick: function onClick() {
        return hasLink && ((item === null || item === void 0 ? void 0 : item.cta_filter) || (item === null || item === void 0 ? void 0 : item.cta_url_path)) && trackEvents("browse_cars", {
          referral_location: "home",
          browse_variant: "hero_area",
          variant_value: item === null || item === void 0 ? void 0 : item.id,
          category_path: null
        });
      }
    }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("picture", null, /*#__PURE__*/React.createElement("source", {
      media: "(min-width: 1025px)",
      srcSet: item === null || item === void 0 ? void 0 : item.banner_img_desktop
    }), /*#__PURE__*/React.createElement("source", {
      media: "(max-width: 1024px)",
      srcSet: item === null || item === void 0 ? void 0 : item.banner_img_mobile
    }), /*#__PURE__*/React.createElement("img", {
      className: "absolute object-cover w-full h-full left-0 top-0",
      src: isMobile ? item === null || item === void 0 ? void 0 : item.banner_img_mobile : item === null || item === void 0 ? void 0 : item.banner_img_desktop,
      alt: (item === null || item === void 0 ? void 0 : item.cta_text) || (isMobile ? item === null || item === void 0 ? void 0 : item.banner_img_mobile : item === null || item === void 0 ? void 0 : item.banner_img_desktop),
      width: isMobile ? "387" : "1287",
      height: isMobile ? "171" : "428",
      loading: loadingType
    }))));
  }), (heros === null || heros === void 0 ? void 0 : heros.length) > 1 && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
    onClick: handlePrev,
    className: ["m-hide", "absolute cursor-pointer top-[calc(50%-34px/2)] box-border rounded-full z-[999] ar:right-6 en:left-6 ltr:rotate-180"].join(" ")
  }, /*#__PURE__*/React.createElement(ProgressiveImage, {
    waitUntilInteractive: true,
    width: 32,
    height: 32,
    fetchpriority: "low",
    src: CDN_LINK + "/assets/images/right-arrow2.svg",
    alt: "a"
  })), /*#__PURE__*/React.createElement("span", {
    onClick: handleNext,
    className: ["m-hide", "absolute cursor-pointer top-[calc(50%-34px/2)] box-border rounded-full z-[999] ar:left-6 ltr:right-6 en:rotate-180"].join(" ")
  }, /*#__PURE__*/React.createElement(ProgressiveImage, {
    waitUntilInteractive: true,
    width: 32,
    height: 32,
    fetchpriority: "low",
    src: CDN_LINK + "/assets/images/leftArrow2.svg",
    alt: "a"
  })), /*#__PURE__*/React.createElement("ul", {
    className: "m-hide flex items-center w-full justify-center mt-5 absolute bottom-[96px] z-[3]"
  }, heros === null || heros === void 0 ? void 0 : heros.map(function (_, idx) {
    return /*#__PURE__*/React.createElement("li", {
      key: "dots" + idx,
      onClick: function onClick() {
        return setActiveIndex(idx);
      },
      className: ["h-[6px] rounded-[3px] bg-[#c5d2e4] w-6 transition-all mx-[2px] cursor-pointer", activeImage === idx ? twMerge("bg-[#fe561c] pointer-events-none") : undefined].join(" ")
    });
  })))));
};
export default HomeBanner;